import React, { useEffect, useState } from "react";
import Navbar from "../../components/layout/navbar";
import { Plus } from "lucide-react";
import useRequest from "../../components/hooks/use-request";
import Button from "../../components/button";
import InventoryMetricsCard from "./metric-card";
import AddProductDialog from "../../components/inventory";
import InventoryTable from "./inventory-table";
import { I_ProductData } from "./types";
import Pagination from "../../components/pagination";

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
}

interface MetricsData {
  totalProducts: number;
  pendingApproval: number;
  outOfStock: number;
  lowStock: number;
}

const Inventory = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [product, setProduct] = useState<I_ProductData[]>([]);
  const userToken = localStorage.getItem("token");
  const [productrModal, setProductrModal] = useState(false);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(
    Number(params.get("page") || 1)
  );
  const [totalPages, setTotalPages] = useState<number>(1);
  const itemsPerPage = 10;
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getMetrics } = useRequest("/inventory/metric", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getProducts } = useRequest("/inventory", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);

    Object.keys(params).forEach((key) => {
      if (params[key]) {
        url.searchParams.set(key, params[key]!.toString());
      } else {
        url.searchParams.delete(key);
      }
    });

    window.history.pushState({}, "", url.toString());
  }

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
    });
    fetchCustomers();
  }, [currentPage]);

  async function fetchCustomers() {
    const page = currentPage;
    const limit = itemsPerPage;

    const [response] = await getProducts(undefined, {
      limit,
      page,
    });

    setProduct(response.data?.inventory?.docs || []);
    setTotalPages(Math.ceil(response.data?.inventory?.totalPages));
  }

  useEffect(() => {
    const fetchMetrics = async () => {
      const [response] = await getMetrics();
      if (response.status) {
        setMetrics(response.data);
      }
    };
    fetchMetrics();
  }, []);

  function closeProductrModal() {
    setProductrModal(false);
  }

  function openProductModal() {
    setProductrModal(true);
  }

  async function handlePageChange(page: number) {
    setCurrentPage(page);
    await fetchCustomers();
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as ProfileData} />

      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-[30px] font-extrabold text-black">
              Inventory Management
            </h1>
            <p className="text-[#676767] text-[18px] font-normal">
              Track and manage your stock levels and suppliers
            </p>
          </div>
          <div className="flex gap-2">
            <button className="rounded-full border px-4 py-1">Export</button>

            <Button
              size="lg"
              variant="primary"
              type="button"
              className="font-normal md:mt-0 mt-6"
              onClick={openProductModal}
            >
              <Plus /> Create Product
            </Button>
          </div>
        </div>

        {metrics && <InventoryMetricsCard metrics={metrics} />}
        <InventoryTable data={product} />

        {totalPages > 1 && (
          <div className="-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </main>
      <AddProductDialog
        isVisible={productrModal}
        onClose={closeProductrModal}
      />
    </div>
  );
};

export default Inventory;
